import Link from 'next/link';
import { PropsWithChildren, useMemo } from 'react';
import NavigateHome from '@/components/icons/NavigateHome';
import { NAVIGATION_FROM_APP, NAVIGATION_FROM_APP_CODE } from '@/constants/common';
import useIsMobile from '@/hooks/useIsMobile';
import useUIQueryParams from '@/hooks/useUIQueryParams';
import { getCookieValue, isDigiAcreUser, parseQueryFromPath, postMessageToWebView } from '@/utils/helper';
import AppBackButton from './AppBackButton';
import CartButton from './cart/MyCart';
import Profile from './navigation/Profile';
import WhatsAppShare from './WhatsAppShare';
import WishlistEntryPoint from './WishlistEntryPoint';
interface Props {
  gaEventData?: string;
  heading: string;
  onBackClick?: VoidFunction;
  showHomeIcon?: boolean;
  showProfileIcon?: boolean;
  showBackButton?: boolean;
  showCart?: boolean;
  showWhatsAppIcon?: boolean;
  whatsappShareMessage?: string;
  homePath?: string;
  headingStyle?: string;
  showWishlistIcon?: boolean;
}
const HeaderWithBackButton = ({
  children,
  gaEventData,
  heading,
  onBackClick,
  showHomeIcon = true,
  showProfileIcon = true,
  showBackButton = true,
  showCart = false,
  showWhatsAppIcon = false,
  showWishlistIcon = false,
  whatsappShareMessage,
  homePath = '/',
  headingStyle = 'grow text-neutral-100 pl-4 sm:p-0 sm:text-center'
}: PropsWithChildren<Props>) => {
  const isDAUser = isDigiAcreUser();
  const fromApp = useMemo(() => Boolean(getCookieValue(NAVIGATION_FROM_APP)) || Boolean(getCookieValue(NAVIGATION_FROM_APP_CODE)) || isDAUser, []);
  const {
    isMobile,
    isWindowLoading
  } = useIsMobile();
  const uiParams = useUIQueryParams();
  const {
    pathname,
    query: existingQuery
  } = useMemo(() => {
    return parseQueryFromPath(homePath);
  }, [homePath]);
  const finalQuery = {
    ...existingQuery,
    ...uiParams
  };
  const handlerOnBackClick = () => {
    if ('ReactNativeWebView' in window) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      postMessageToWebView(window, {
        code: 'backClick',
        message: window.location.pathname
      });
    }
    onBackClick?.();
  };
  return <div data-sentry-component="HeaderWithBackButton" data-sentry-source-file="HeaderWithBackButton.tsx">
      <header className={`bg-white px-4 flex items-center shadow-[0_6px_20px_rgba(0,0,0,0.05)] fixed top-0 w-full left-0 z-50 ${!showProfileIcon || fromApp && !isDAUser ? 'py-4' : 'pb-2 pt-2.5'}`}>
        {showBackButton && <AppBackButton gaEventData={gaEventData}
      // Without a page handler, use AppBackButton's default (history back,
      // else home) instead of a no-op. The app webview keeps the
      // backClick-message-only behaviour its native side relies on.
      onBackClick={onBackClick || typeof window !== 'undefined' && 'ReactNativeWebView' in window ? handlerOnBackClick : undefined} />}
        {showHomeIcon && !fromApp && <Link href={{
        pathname,
        query: finalQuery
      }}>
            <NavigateHome className="fill-primary-100 ml-4 h-6" aria-label="Home" />
          </Link>}
        <h5 className={headingStyle}>{heading}</h5>
        {children}
        {showWishlistIcon && isMobile && <WishlistEntryPoint />}
        {showCart && !isWindowLoading && !isMobile && <CartButton />}
        {showProfileIcon && !fromApp && <Profile />}
        {showWhatsAppIcon && <WhatsAppShare message={whatsappShareMessage as string} />}
      </header>
    </div>;
};
export default HeaderWithBackButton;